<template>
  <div
    class="mx-auto"
    :class="{ 'w-full': isScheduleMessageCard || isConferenceCreatedWelcome }"
  >
    <div
      v-if="isScheduleMessageCard"
      class="flex justify-center"
      data-test="chat-event-schedule-card"
    >
      <event-card-for-chat
        :scheduled-event="scheduledEvent"
        @edit="openEditEventDialog"
      />
    </div>
    <chat-conference-created-welcome
      v-else-if="isConferenceCreatedWelcome && room"
      :room="room"
    />
    <chat-notification
      v-if="!isScheduleMessageCard"
      :title="title"
      class="mx-auto"
    >
      <div class="flex max-w-full flex-wrap items-center justify-center gap-x-1 text-center">
        <span
          v-if="executorName"
          class="font-medium text-fg-base-fg3"
          data-test="chat-system-message-executor"
        >
          {{ executorName }}
        </span>
        <span
          class="truncate"
          data-test="chat-system-message"
          :class="{'cursor-pointer' : event.item.type === 'ConferenceGuestAccessEnabled'}"
          @click="copyLinkForGuestAccess"
        >
          {{ messageText }}
        </span>
      </div>
    </chat-notification>
    <div
      v-if="isUnread"
      class="relative"
    >
      <span class="absolute right-[-10px] bottom-0 h-2 w-2 rounded-full bg-primary-tints-400" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDialog, useRenderNotifier } from '@wui/common-library'
import {
  AnyConferenceChangedByOtherEvent,
  AnyConferenceChangedByYouEvent,
  ConferenceScheduleChangedByOtherEvent,
  ConferenceScheduleChangedByYouEvent,
  ConferenceScheduleDisabledByOtherEvent,
  ConferenceScheduleDisabledByYouEvent,
  ConferenceScheduleUpdatedByOtherEvent,
  ConferenceScheduleUpdatedByYouEvent,
  HistoryMessageItem,
} from '@wui/im'
import { computed } from 'vue'

import { ScheduleEditDialog, ScheduleEditErrorDialog, ScheduleEditSuccessDialog } from '@/widgets/room-schedule-dialog'

import { EventCardForChat } from '@/features/event-card-for-chat'
import type { FormattedEvent } from '@/features/group-events-list'

import { getSystemMessageText } from '@/entities/chat-system-message'
import { useStoreContacts } from '@/entities/contact'
import { useSafeUnreadState } from '@/entities/messages'
import { useStoreRooms } from '@/entities/room'

import { ChatNotification } from '@/shared/components/chat-notification'
import { useCurrentUser } from '@/shared/composables/current-user'
import { useLocalization } from '@/shared/composables/localization'
import { getDayJSInTimezone } from '@/shared/composables/timezone'
import { TIME_FORMAT, UI_DATE_FORMAT } from '@/shared/utils/constants'
import { getInviteLoginAbsoluteURL } from '@/shared/utils/url'

import ChatConferenceCreatedWelcome from './chat-conference-created-welcome.vue'

const props = defineProps<{
  event: HistoryMessageItem<AnyConferenceChangedByOtherEvent | AnyConferenceChangedByYouEvent>
}>()

type ConferenceScheduleCardMessage = HistoryMessageItem<
  | ConferenceScheduleChangedByOtherEvent
  | ConferenceScheduleChangedByYouEvent
  | ConferenceScheduleDisabledByOtherEvent
  | ConferenceScheduleDisabledByYouEvent
  | ConferenceScheduleUpdatedByOtherEvent
  | ConferenceScheduleUpdatedByYouEvent
>

const isConferenceScheduleCardMessage = (
  event: HistoryMessageItem<AnyConferenceChangedByOtherEvent | AnyConferenceChangedByYouEvent>,
): event is ConferenceScheduleCardMessage => {
  const type = event.item.type
  if (type === 'ConferenceScheduleChanged' || type === 'ConferenceScheduleUpdated') {
    return true
  }

  // Для отмены карточка нужна только если в событии есть ICS с деталями
  return type === 'ConferenceScheduleDisabled' && Boolean(event.item.schedule?.events?.length)
}

const { notifySuccess } = useRenderNotifier()
const { showDialog } = useDialog()
const { findByPServed } = useStoreRooms()
const { findByPServed: findContactByPServed } = useStoreContacts()
const { currentUser } = useCurrentUser()
const { t } = useLocalization()

const messageText = computed(() => {
  return getSystemMessageText(props.event)
})

const room = computed(() => findByPServed(props.event.item.pServed))

const isConferenceCreatedWelcome = computed(() => (
  props.event.item.type === 'ConferenceCreated' && Boolean(room.value?.isGroup)
))

const title = computed(() => {
  if (!props.event.timestamp) {
    return ''
  }

  return getDayJSInTimezone(props.event.timestamp).format(`${UI_DATE_FORMAT} ${TIME_FORMAT}`)
})

const contact = computed(() => {
  if (!props.event.item.executor) {
    return undefined
  }
  if (currentUser.value?.imLogin === props.event.item.executor) {
    return currentUser.value
  }
  return findContactByPServed(props.event.item.executor)
})

const executorName = computed(() => contact.value?.name ?? '')

const scheduledEvent = computed<ConferenceScheduleCardMessage>(() => {
  if (isConferenceScheduleCardMessage(props.event)) {
    return props.event
  }
  throw new Error('Invalid event type for EventCardForChat')
})

const isScheduleMessageCard = computed(() => isConferenceScheduleCardMessage(props.event))

const copyLinkForGuestAccess = () => {
  if (props.event.item.type !== 'ConferenceGuestAccessEnabled') {
    return
  }
  const systemMessageWithLink = props.event.item
  navigator.clipboard.writeText(getInviteLoginAbsoluteURL({
    roomPServed: systemMessageWithLink.pServed,
    roomToken: systemMessageWithLink.token,
  }))
  notifySuccess(t('mainAside.copiedToClipboard'))
}

const { isUnread } = useSafeUnreadState(props.event)

const openEditEventDialog = async (event: FormattedEvent) => {
  const response = await showDialog(ScheduleEditDialog, {
    pServed: scheduledEvent.value.item.pServed,
    hasOverlay: true,
    event,
    eventId: event.uid,
  }) as { error: Error } | { pServed: string, participants: string[], uid: string } | boolean

  if (typeof response !== 'object' || response === null) {
    return
  }

  if ('error' in response) {
    showDialog(ScheduleEditErrorDialog, {
      error: response.error,
      hasOverlay: true,
    })
    return
  }

  if ('pServed' in response) {
    showDialog(ScheduleEditSuccessDialog, {
      pServed: response.pServed,
      participantsNumbers: response.participants,
      hasOverlay: true,
      uid: response.uid,
    })
  }
}
</script>

<style scoped>
/* title для мобильных устройств */
@media (pointer: coarse), (hover: none) {
  [title] {
    position: relative;
    display: inline-flex;
    justify-content: center;
  }
  [title]:focus::after {
    content: attr(title);
    position: absolute;
    top: 90%;
    color: #000;
    background-color: #fff;
    border: 1px solid;
    width: fit-content;
    padding: 3px;
    font-size: 10px;
  }
}
</style>

